/**
 * Inline preview of one SELECTED attachment inside the print window — shows
 * what will actually merge into the printed PDF (images/PDFs), or notes
 * that a non-mergeable file only rides along on the emailed copy. No zoom
 * controls (this is a review block, not the Files-tab preview pane).
 */
import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import apiClient from '@/utils/api'
import { SAFE_PREVIEW_IMAGE_TYPES, canPreviewInline, PDF_MAGIC, blobStartsWith } from './filePreview'
import type { Attachment } from '../AttachmentPanel'
import { isMergeableAttachment } from './usePrintAttachments'

export interface PrintAttachmentPreviewProps {
  connectionId: string
  attachment: Attachment
}

export function PrintAttachmentPreview({ connectionId, attachment }: PrintAttachmentPreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewText, setPreviewText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setPreviewUrl(null)
    setPreviewText(null)
    setError(null)
    if (!canPreviewInline(attachment.content_type)) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const res = await apiClient.get(
          `/v1/attachments/download/${attachment.id}?connection_id=${encodeURIComponent(connectionId)}`,
          { responseType: 'blob' },
        )
        const blob = res.data as Blob
        if (cancelled) return
        if (attachment.content_type === 'text/plain') {
          const text = await blob.text()
          if (cancelled) return
          setPreviewText(text)
        } else if (attachment.content_type === 'application/pdf') {
          const isPdf = await blobStartsWith(blob, PDF_MAGIC)
          if (cancelled) return
          if (isPdf) {
            setPreviewUrl(URL.createObjectURL(blob))
          } else {
            setError("This file is named .pdf but its content isn't a valid PDF.")
          }
        } else {
          setPreviewUrl(URL.createObjectURL(blob))
        }
      } catch (e) {
        if (!cancelled) setError((e as Error)?.message || 'Failed to load preview')
      }
      if (!cancelled) setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [connectionId, attachment.id, attachment.content_type])

  useEffect(() => {
    if (!previewUrl) return
    return () => URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  return (
    <Box sx={{ mb: 2 }} data-testid={`print-attachment-preview-${attachment.id}`}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {attachment.file_name}
      </Typography>
      {loading ? (
        <CircularProgress size={20} />
      ) : error ? (
        <Typography variant="body2" color="error">{error}</Typography>
      ) : previewText !== null ? (
        <Box
          component="pre"
          sx={{
            p: 1, bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: 12,
            whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto',
          }}
        >
          {previewText}
        </Box>
      ) : previewUrl && SAFE_PREVIEW_IMAGE_TYPES.has(attachment.content_type) ? (
        <Box component="img" src={previewUrl} alt={attachment.file_name} sx={{ maxWidth: '100%', display: 'block' }} />
      ) : previewUrl && attachment.content_type === 'application/pdf' ? (
        <Box component="object" data={previewUrl} type="application/pdf" sx={{ width: '100%', height: 500 }} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {isMergeableAttachment(attachment.content_type)
            ? 'No inline preview available for this file type.'
            : 'Not merged into the printed document — will be attached to the email only.'}
        </Typography>
      )}
    </Box>
  )
}
